import { ArrowRightIcon, ChevronDownIcon, DownloadIcon, InfoIcon, LockIcon, ShieldIcon } from 'core/components/Icons'
import { SamarthLogo } from 'core/components/Logo/SamarthLogo'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

interface SectionItem {
  id: string
  title: string
}

const SECTIONS: SectionItem[] = [
  { id: 'overview', title: '1. Overview & Applicability' },
  { id: 'data-we-collect', title: '2. KYC & Data We Collect' },
  { id: 'backend-storage', title: '3. Backend Storage & Security' },
  { id: 'payment-security', title: '4. Payment Security & UPI' },
  { id: 'device-permissions', title: '5. Hardware & Device Permissions' },
  { id: 'microsoft-clarity', title: '6. Microsoft Clarity & Masking' },
  { id: 'data-sharing', title: '7. Third-Party Sharing' },
  { id: 'user-rights-deletion', title: '8. User Rights & Data Deletion' },
  { id: 'children-privacy', title: "9. Children's Privacy" },
  { id: 'contact-grievance', title: '10. Contact & Corporate Details' },
]

export const PrivacyPolicyPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('overview')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      if (scrollY + windowHeight >= documentHeight - 60) {
        setActiveSection(SECTIONS[SECTIONS.length - 1].id)
        return
      }

      const threshold = 180
      let currentSection = SECTIONS[0].id

      for (const section of SECTIONS) {
        const el = document.getElementById(section.id)
        if (el) {
          const rect = el.getBoundingClientRect()
          if (rect.top <= threshold) {
            currentSection = section.id
          }
        }
      }

      setActiveSection(currentSection)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollTo = (id: string) => {
    setActiveSection(id)
    setMobileMenuOpen(false)
    const element = document.getElementById(id)
    if (element) {
      const yOffset = -90
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="min-h-screen bg-bg text-text-primary transition-colors duration-300">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-surface/90 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
            <SamarthLogo showText={true} size="small" layout="row" />
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-btn border border-border bg-surface hover:bg-bg transition-colors"
              title="Print or save as PDF"
            >
              <DownloadIcon size={14} />
              Print / Save PDF
            </button>
            <Link
              to="/terms-of-service"
              className="text-xs font-semibold px-3 py-2 rounded-btn text-text-secondary hover:text-text-primary transition-colors"
            >
              Terms of Service
            </Link>
            <a
              href="https://samarthagri.com"
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold px-3 py-2 rounded-btn text-text-secondary hover:text-text-primary transition-colors"
            >
              Company Website
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-btn bg-primary text-primary-fg hover:brightness-110 shadow-soft transition-all"
            >
              Sign In
              <ArrowRightIcon size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-surface border-b border-border py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary mb-3">
            <ShieldIcon size={14} />
            Official Compliance Document
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-text-primary">
            Privacy Policy
          </h1>
          <p className="mt-3 text-base sm:text-lg text-text-secondary max-w-3xl mx-auto">
            Sanjeevani Setu Mobile Application (<code>com.sanjeevani.setu</code>)
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs font-medium text-text-secondary">
            <span>
              <strong>Entity:</strong> Samarth EduSkills Pvt. Ltd. (Samarth Agri)
            </span>
            <span>•</span>
            <span>
              <strong>Website:</strong> https://samarthagri.com
            </span>
            <span>•</span>
            <span>
              <strong>Version:</strong> 1.0 (Google Play Compliant)
            </span>
          </div>
        </div>
      </div>

      {/* Quick Summary Highlights */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-surface rounded-card border border-border p-6 shadow-card">
          <h2 className="text-sm font-bold uppercase tracking-wider text-text-secondary mb-4 flex items-center gap-2">
            <InfoIcon size={18} className="text-primary" />
            Key Privacy Commitments at a Glance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-btn bg-bg border border-border/70">
              <div className="text-primary font-bold text-sm mb-1">🔒 KYC & Storage</div>
              <p className="text-xs text-text-secondary leading-relaxed">
                PAN, GST, and business documents are encrypted in transit and stored in restricted cloud storage. Auto
                cloud backup is disabled.
              </p>
            </div>
            <div className="p-4 rounded-btn bg-bg border border-border/70">
              <div className="text-primary font-bold text-sm mb-1">💳 Payment Security</div>
              <p className="text-xs text-text-secondary leading-relaxed">
                Executed via native UPI Intent hand-off directly inside your bank app. We never store ATM PINs, bank
                passwords, or card numbers.
              </p>
            </div>
            <div className="p-4 rounded-btn bg-bg border border-border/70">
              <div className="text-primary font-bold text-sm mb-1">🛡️ Clarity Masking</div>
              <p className="text-xs text-text-secondary leading-relaxed">
                Used for usability analytics. All sensitive screens (identity documents, PAN, GSTIN, payment slips) are
                strictly masked and excluded.
              </p>
            </div>
            <div className="p-4 rounded-btn bg-bg border border-border/70">
              <div className="text-primary font-bold text-sm mb-1">🗑️ Right to Deletion</div>
              <p className="text-xs text-text-secondary leading-relaxed">
                Users can permanently delete their account and personal profile anytime by contacting
                business@samarthagri.com.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Desktop Table of Contents */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-24">
            <div className="bg-surface rounded-card border border-border p-5 shadow-card">
              <h3 className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-3">Table of Contents</h3>
              <nav className="space-y-1">
                {SECTIONS.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => scrollTo(sec.id)}
                    className={`w-full text-left px-3 py-2 rounded-btn text-xs font-semibold transition-all flex items-center justify-between ${
                      activeSection === sec.id
                        ? 'bg-primary text-primary-fg shadow-soft font-bold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg'
                    }`}
                  >
                    <span>{sec.title}</span>
                  </button>
                ))}
              </nav>

              <div className="mt-6 pt-5 border-t border-border">
                <Link
                  to="/terms-of-service"
                  className="flex items-center justify-between text-xs font-semibold text-primary hover:underline"
                >
                  <span>View Terms of Service</span>
                  <ArrowRightIcon size={12} />
                </Link>
              </div>
            </div>
          </aside>

          {/* Mobile Table of Contents */}
          <div className="lg:hidden col-span-1">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-surface border border-border rounded-btn text-sm font-semibold"
            >
              <span>Jump to Section</span>
              <ChevronDownIcon
                size={16}
                className={`transform transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {mobileMenuOpen && (
              <div className="mt-2 p-3 bg-surface border border-border rounded-card shadow-card space-y-1">
                {SECTIONS.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => scrollTo(sec.id)}
                    className={`w-full text-left px-3 py-2 rounded-btn text-xs font-semibold transition-all ${
                      activeSection === sec.id
                        ? 'bg-primary text-primary-fg font-bold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg'
                    }`}
                  >
                    {sec.title}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Legal Clauses */}
          <main className="lg:col-span-8 bg-surface rounded-card border border-border p-6 sm:p-10 shadow-card space-y-10">
            {/* 1. Overview */}
            <section id="overview" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                1. Overview & Applicability
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  This Privacy Policy describes the policies and practices of{' '}
                  <strong>Samarth EduSkills Pvt. Ltd. (Samarth Agri)</strong> (&quot;we&quot;, &quot;us&quot;, or
                  &quot;our&quot;) regarding the collection, storage, use, and protection of information when you use
                  the <strong>Sanjeevani Setu</strong> mobile application (Package ID: <code>com.sanjeevani.setu</code>)
                  and associated platform services.
                </p>
                <p>
                  <strong>Official Website:</strong>{' '}
                  <a
                    href="https://samarthagri.com"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline font-semibold"
                  >
                    https://samarthagri.com
                  </a>
                </p>
                <p>
                  Sanjeevani Setu provides an integrated commerce and management ecosystem for rural animal care
                  coordinators, dairy farmers, livestock entrepreneurs, veterinary product distributors, and retail
                  dealers.
                </p>
              </div>
            </section>

            {/* 2. KYC Data */}
            <section id="data-we-collect" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                2. Information Collected at KYC Registration
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-4">
                <p>
                  To onboard commercial dealers, distributors, and agents and comply with Indian commercial laws, we
                  collect:
                </p>
                <div className="space-y-3">
                  <div className="p-4 rounded-btn bg-bg border border-border">
                    <h4 className="font-bold text-text-primary text-sm mb-1">A. Business & Tax Identifiers</h4>
                    <p className="text-xs">
                      Permanent Account Number (PAN), Goods and Services Tax Identification Number (GSTIN), proprietor /
                      authorized contact name, dealership / trade entity name, mobile phone number, email address, and
                      warehouse delivery address.
                    </p>
                  </div>
                  <div className="p-4 rounded-btn bg-bg border border-border">
                    <h4 className="font-bold text-text-primary text-sm mb-1">
                      B. KYC Document Uploads & Accepted Formats
                    </h4>
                    <p className="text-xs">
                      Scanned or photographed copies of the dealer&apos;s PAN card and GST registration certificate. The
                      mobile application strictly supports and accepts only the following formats:
                    </p>
                    <ul className="list-disc pl-5 mt-2 space-y-1 text-xs">
                      <li>
                        <strong>PDF Documents:</strong> <code>.pdf</code> format selected via the device document
                        picker.
                      </li>
                      <li>
                        <strong>Image Files:</strong> <code>.jpg</code>, <code>.jpeg</code>, and <code>.png</code>{' '}
                        formats captured live using the device camera or selected from device gallery.
                      </li>
                    </ul>
                  </div>
                  <div className="p-4 rounded-btn bg-bg border border-border">
                    <h4 className="font-bold text-text-primary text-sm mb-1">C. What We Explicitly Do Not Collect</h4>
                    <p className="text-xs">
                      The application does <strong>NOT</strong> collect or request GPS location coordinates, drug or
                      pharmacy distribution licenses, Aadhaar card credentials, banking passwords, ATM PINs,
                      debit/credit card security numbers (CVV), or device call logs and contact books.
                    </p>
                  </div>
                  <div className="p-4 rounded-btn bg-bg border border-border">
                    <h4 className="font-bold text-text-primary text-sm mb-1">D. Affirmative Consent</h4>
                    <p className="text-xs">
                      Before uploading documents, users are presented with an affirmative consent declaration. Documents
                      are submitted strictly upon affirmative user action.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. Backend Storage */}
            <section id="backend-storage" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                3. How Data is Stored and Protected in the Backend
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>We implement strict enterprise-grade security controls for all backend data storage:</p>
                <ul className="list-disc pl-5 space-y-2 text-xs">
                  <li>
                    <strong>Encryption in Transit:</strong> All communication between the mobile app and our backend
                    APIs is encrypted using TLS 1.3 / HTTPS protocols.
                  </li>
                  <li>
                    <strong>Encrypted Storage Buckets:</strong> KYC documents and transaction slips are stored in secure
                    cloud storage buckets with encryption at rest (AES-256).
                  </li>
                  <li>
                    <strong>Role-Based Access Control (RBAC):</strong> Only authorized KYC verification officers have
                    access to view documents. Documents are never exposed to unauthorized employees or external third
                    parties.
                  </li>
                  <li>
                    <strong>Cloud Auto-Backup Disabled:</strong> The mobile application explicitly disables automated
                    Google Drive cloud backups (<code>allowBackup=&quot;false&quot;</code>) to prevent cached documents
                    and tokens from syncing to personal cloud drives.
                  </li>
                </ul>
              </div>
            </section>

            {/* 4. Payment Security */}
            <section id="payment-security" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                4. Payment Security & UPI Architecture
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-4">
                <p>
                  When ordering cattle feed and agricultural products, payments are secured through official banking
                  channels:
                </p>
                <div className="p-4 rounded-btn bg-bg border border-border space-y-2 text-xs">
                  <p>
                    <strong>
                      Native UPI Intent (<code>upi://pay</code>):
                    </strong>{' '}
                    Payment requests hand off directly to licensed bank applications (Google Pay, PhonePe, Paytm, BHIM)
                    installed on your device over NPCI&apos;s encrypted payment network.
                  </p>
                  <p>
                    <strong>Zero Financial Credentials Stored:</strong> Sanjeevani Setu never handles, accesses, or
                    stores ATM PINs, net banking passwords, credit/debit card numbers, or CVVs.
                  </p>
                  <p>
                    <strong>Ledger Reconciliation:</strong> Only non-sensitive reference information (UTR transaction
                    reference number and uploaded receipt screenshot) is transmitted to the backend to verify order
                    settlement.
                  </p>
                </div>
              </div>
            </section>

            {/* 5. Device Permissions */}
            <section id="device-permissions" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                5. Device Hardware & Runtime Permissions
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs text-left border border-border rounded-btn overflow-hidden">
                    <thead className="bg-bg font-bold text-text-primary">
                      <tr>
                        <th className="py-2.5 px-3 border-b border-border">Permission</th>
                        <th className="py-2.5 px-3 border-b border-border">Plugin</th>
                        <th className="py-2.5 px-3 border-b border-border">Operational Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr>
                        <td className="py-2 px-3 font-semibold text-text-primary">Camera</td>
                        <td className="py-2 px-3">
                          <code>image_picker</code>
                        </td>
                        <td className="py-2 px-3">
                          Captures photos of physical KYC documents (PAN, GSTIN), payment slips, and damaged shipments
                          when filing grievances.
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-semibold text-text-primary">File Picker</td>
                        <td className="py-2 px-3">
                          <code>file_picker</code>
                        </td>
                        <td className="py-2 px-3">
                          Allows selecting PDF certificates or images from device storage. Broad storage access is
                          stripped via <code>tools:node=&quot;remove&quot;</code>.
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-semibold text-text-primary">Push Notifications</td>
                        <td className="py-2 px-3">
                          <code>firebase_messaging</code>
                        </td>
                        <td className="py-2 px-3">
                          Used on Android 13+ to send transactional alerts for order dispatch, shipment status, and KYC
                          approval/rejection outcomes.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* 6. Clarity */}
            <section id="microsoft-clarity" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border flex items-center gap-2">
                <LockIcon size={20} className="text-primary" />
                6. Microsoft Clarity Session Recording & Masking
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-4">
                <div className="p-4 rounded-btn bg-primary/10 border border-primary/20 text-text-primary">
                  <p className="font-semibold text-sm mb-1">Session Recording Disclosure</p>
                  <p className="text-xs text-text-secondary leading-relaxed">
                    We use <strong>Microsoft Clarity</strong>, a session recording tool, to understand how users
                    interact with the app. Clarity records screen interactions and sends anonymised session data to
                    Microsoft servers. Identity documents and sensitive screens are excluded from recording.
                  </p>
                </div>
                <h4 className="font-bold text-text-primary text-sm">Sensitive Screen Masking:</h4>
                <ul className="list-disc pl-5 space-y-2 text-xs">
                  <li>
                    <strong>Strict KYC Masking:</strong> Identity document upload cards, PAN numbers, GSTIN details, and
                    document preview modals are strictly wrapped in <code>ClarityMask</code> and excluded from
                    recording.
                  </li>
                  <li>
                    <strong>Financial Exclusion:</strong> UPI payment IDs and invoice slips are never captured in plain
                    text.
                  </li>
                  <li>
                    <strong>No Behavioral Ads:</strong> Clarity telemetry is used solely for technical usability and is
                    never sold or used for advertising.
                  </li>
                </ul>
              </div>
            </section>

            {/* 7. Third-Party Sharing */}
            <section id="data-sharing" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                7. Third-Party Service Providers
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>We disclose information only to vetted service providers for technical operations:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-bg border border-border rounded-btn">
                    <strong>Microsoft Clarity:</strong> Anonymized interaction telemetry & analytics.
                  </div>
                  <div className="p-3 bg-bg border border-border rounded-btn">
                    <strong>Google Firebase:</strong> Cloud infrastructure and push notifications (FCM).
                  </div>
                  <div className="p-3 bg-bg border border-border rounded-btn">
                    <strong>NPCI / UPI Apps:</strong> Secure hand-off to installed banking apps.
                  </div>
                  <div className="p-3 bg-bg border border-border rounded-btn">
                    <strong>Statutory Authorities:</strong> Where legally mandated by applicable law.
                  </div>
                </div>
              </div>
            </section>

            {/* 8. User Rights */}
            <section id="user-rights-deletion" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border flex items-center gap-2">
                <ShieldIcon size={20} className="text-primary" />
                8. Your Rights & Data Deletion
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-4">
                <p>
                  In accordance with Google Play Developer Policies, all users have the right to request access to or
                  complete deletion of their account and personal data at any time:
                </p>
                <div className="p-4 rounded-btn bg-bg border border-border space-y-2 text-xs">
                  <p className="font-bold text-text-primary text-sm">How to Request Account & Data Deletion:</p>
                  <p>
                    Send an email from your registered email address or phone to{' '}
                    <a href="mailto:business@samarthagri.com" className="text-primary font-bold hover:underline">
                      business@samarthagri.com
                    </a>{' '}
                    with the subject line <code>&quot;Data Deletion Request - Sanjeevani Setu&quot;</code>.
                  </p>
                  <p>
                    Our compliance team will verify your identity and permanently delete your credentials, profile data,
                    and session logs within <strong>30 calendar days</strong>.
                  </p>
                  <p className="text-text-secondary">
                    <em>
                      (Statutory accounting records and tax invoices required by Indian accounting regulations will be
                      retained in restricted archival storage for the mandated legal audit period).
                    </em>
                  </p>
                </div>
              </div>
            </section>

            {/* 9. Children's Privacy */}
            <section id="children-privacy" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                9. Children&apos;s Privacy
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-2">
                <p>
                  The Sanjeevani Setu platform is strictly designed for commercial, business, and livestock management
                  use by individuals aged <strong>18 and older</strong>. We do not knowingly solicit or collect data
                  from minors.
                </p>
              </div>
            </section>

            {/* 10. Contact */}
            <section id="contact-grievance" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                10. Contact & Corporate Details
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-4">
                <p>For inquiries, support, or privacy grievances, contact our compliance team:</p>
                <div className="p-5 rounded-btn bg-bg border border-border space-y-2 text-xs">
                  <p className="font-bold text-text-primary text-sm">Samarth EduSkills Pvt. Ltd. (Samarth Agri)</p>
                  <p>
                    <strong>Application:</strong> Sanjeevani Setu (Android: <code>com.sanjeevani.setu</code>)
                  </p>
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:business@samarthagri.com" className="text-primary font-semibold hover:underline">
                      business@samarthagri.com
                    </a>
                  </p>
                  <p>
                    <strong>Phone:</strong> 0124-4165404
                  </p>
                  <p>
                    <strong>Website:</strong>{' '}
                    <a
                      href="https://samarthagri.com"
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary font-semibold hover:underline"
                    >
                      https://samarthagri.com
                    </a>
                  </p>
                  <p>
                    <strong>Corporate Address:</strong> A-210, Arcadia, Sector 49 South City, Gurgaon, Haryana - 122018,
                    India
                  </p>
                  <p>
                    <strong>Operating Hours:</strong> Monday – Saturday, 9:30 AM to 6:30 PM IST
                  </p>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-surface py-8 px-4 sm:px-6 lg:px-8 text-center text-xs text-text-secondary">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} Samarth EduSkills Pvt. Ltd. (Samarth Agri). All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/terms-of-service" className="hover:text-primary transition-colors font-semibold">
              Terms of Service
            </Link>
            <a
              href="https://samarthagri.com"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary transition-colors font-semibold"
            >
              samarthagri.com
            </a>
            <Link to="/login" className="hover:text-primary transition-colors font-semibold">
              Admin Portal
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
